import React, { useEffect, useRef, useState } from 'react';

/**
 * AnimatedSection - Reusable scroll animation wrapper component
 * Optimized for performance and SEO-friendly (content is always in DOM)
 */
const AnimatedSection = ({ 
  children, 
  animation = 'fade-up', // fade-up, slide-left, slide-right, scale-up, fade-in
  delay = 0,
  threshold = 0.1,
  className = '',
  as: Component = 'div',
  ...props 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Add small delay for staggered effect if specified
          if (delay > 0) {
            setTimeout(() => setIsVisible(true), delay);
          } else {
            setIsVisible(true);
          }
          observer.disconnect();
        }
      },
      { threshold, rootMargin: '50px' }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, [threshold, delay]);

  // Map animation types to CSS classes
  const animationClasses = {
    'fade-up': 'animate-on-scroll',
    'slide-left': 'animate-slide-left',
    'slide-right': 'animate-slide-right',
    'scale-up': 'animate-scale-up',
    'fade-in': 'animate-fade-in',
    'reveal-up': 'animate-reveal-up',
  };

  const animationClass = animationClasses[animation] || 'animate-on-scroll';

  return (
    <Component
      ref={elementRef}
      className={`${animationClass} ${isVisible ? 'visible' : ''} ${className}`}
      {...props}
    >
      {children}
    </Component>
  );
};

/**
 * StaggeredContainer - Animates children with staggered delays
 */
export const StaggeredContainer = ({ 
  children, 
  staggerDelay = 100,
  threshold = 0.1,
  className = '',
  ...props 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [threshold]);

  return (
    <div
      ref={containerRef}
      className={`animate-stagger ${isVisible ? 'visible' : ''} ${className}`}
      {...props}
    >
      {React.Children.map(children, (child, index) => (
        <div 
          style={{ 
            transitionDelay: isVisible ? `${index * staggerDelay}ms` : '0ms',
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          {child}
        </div>
      ))}
    </div>
  );
};

/**
 * CountUpNumber - Animated counter for statistics
 */
export const CountUpNumber = ({ 
  end, 
  duration = 2000, 
  suffix = '',
  prefix = '',
  className = '' 
}) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    let startTime;
    const numericEnd = parseFloat(String(end).replace(/[^\d.-]/g, '')) || 0;
    
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * numericEnd));

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(numericEnd);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return (
    <span ref={elementRef} className={`counter-number ${className}`}>
      {prefix}{count}{suffix}
    </span>
  );
};

/**
 * ParallaxSection - Simple parallax effect on scroll
 */
export const ParallaxSection = ({ 
  children, 
  speed = 0.5, 
  className = '',
  ...props 
}) => {
  const [offset, setOffset] = useState(0);
  const elementRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (elementRef.current) {
        const rect = elementRef.current.getBoundingClientRect();
        const scrolled = window.pageYOffset;
        const elementTop = rect.top + scrolled;
        const parallaxOffset = (scrolled - elementTop) * speed;
        setOffset(parallaxOffset);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [speed]);

  return (
    <div
      ref={elementRef}
      className={`gpu-accelerate ${className}`}
      style={{ transform: `translateY(${offset}px)` }}
      {...props}
    >
      {children}
    </div>
  );
};

export default AnimatedSection;
